import React from 'react';
import { Link, useParams } from "react-router-dom";
import { BookOpen, ArrowRight, Lock } from 'lucide-react';
import InstallationSkeleton from '../../components/skeletons/pages/InstallationSkeleton';
import HavingTroubleCard from './HavingTroubleCard';
import APIKeysSection from './APIKeysSection';
import { useOrganisation } from '../../http-hooks/organisation/useOrganisation';
import { useIntegration } from '../../http-hooks/integration/useIntegration';
import { Card, PageHeader } from '../../components/ui/bootstrap';

function Listen2ItAPI() {

    const { org_id, id } = useParams();

    const { data: org, isLoading: isLoadingOrg } = useOrganisation({ org_id });
    const { data: integration, isLoading: isLoadingIntegration } = useIntegration({ org_id, integration_id: id });

    const pageHeader = (
        <PageHeader
            title="Listen2It API"
            subtitle="Build custom integrations with your website or app"
        />
    );

    if (isLoadingOrg || isLoadingIntegration) {
        return (
            <div>
                {pageHeader}
                <InstallationSkeleton />
            </div>
        );
    }

    if (!org?.gates?.can_access_public_api) {
        return (
                <div>
                    {pageHeader}
                <Card className="rounded-4 p-5 text-center" shadow>
                    <div
                        className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-4"
                        style={{ width: 64, height: 64, background: 'rgba(var(--bs-primary-rgb), 0.08)' }}
                    >
                        <Lock size={28} color="var(--bs-primary)" strokeWidth={1.5} />
                    </div>
                    <h5 className="fw-bold mb-2 text-body-emphasis">API access requires a paid plan</h5>
                    <p className="text-muted mb-4" style={{ fontSize: '0.9rem', maxWidth: 420, margin: '0 auto 1.5rem' }}>
                        Using the Listen2It API you can fetch, create, edit and disable audios programmatically. Upgrade to unlock full API access.
                    </p>
                    <Link
                        to={`/organisation/${org_id}/upgrade?source=api_access_link`}
                        className="btn btn-primary d-inline-flex align-items-center gap-2"
                        style={{ borderRadius: 50, padding: '10px 28px' }}
                    >
                        View Plans <ArrowRight size={16} />
                    </Link>
                </Card>
            </div>
        );
    }

    return (
        <div>
            {pageHeader}

            {/* About + docs link */}
            <Card className="rounded-4 p-4 mb-4 d-flex align-items-start gap-4 flex-wrap" shadow>
                <div
                    className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: 40, height: 40, background: 'rgba(var(--bs-secondary-rgb), 0.12)' }}
                >
                    <BookOpen size={20} color="var(--bs-secondary)" strokeWidth={1.75} />
                </div>
                <div className="flex-grow-1">
                    <h6 className="fw-semibold mb-1 text-body-emphasis">REST API</h6>
                    <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                        Use our REST API to build custom integrations. Fetch, create, edit and disable audios programmatically from any platform.
                    </p>
                    <a
                        href={process.env.REACT_APP_PUBLIC_API_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm d-inline-flex align-items-center gap-2"
                        style={{
                            borderRadius: 50,
                            background: 'rgba(var(--bs-primary-rgb), 0.08)',
                            border: '1px solid rgba(var(--bs-primary-rgb), 0.2)',
                            color: 'var(--bs-primary)',
                            fontWeight: 500,
                            fontSize: 13,
                            padding: '6px 16px',
                        }}
                    >
                        <BookOpen size={14} />
                        API Documentation <ArrowRight size={13} />
                    </a>
                </div>
            </Card>

            <APIKeysSection orgId={org_id} id={id} integration={integration} />
            <div className="mt-4">
                <HavingTroubleCard />
            </div>
        </div>
    );
}

export default Listen2ItAPI;

